import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { isDefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import * as ClearancePermitListState from '../../clearance-permit-list/state/ClearancePermitListState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { FullSlideIn } from '../../ui/slidein/Slidein.tsx';
import * as AddressState from '../state/AddressState.ts';
import * as LicensePlateState from '../state/LicensePlateState.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import { PermitCreate } from './PermitCreate.tsx';
import { PermitEdit } from './PermitEdit.tsx';

interface EditContextEnabledProps {
    permitCreateLayout: PermitCreateState.Layout.State;
    clearancePermitList: ClearancePermitListState.State;
}
export const editContextEnabled = (s: EditContextEnabledProps): boolean => {
    const permitCreateLayout = s.permitCreateLayout;
    return (
        !permitCreateLayout.createEnabled &&
        isDefined(s.clearancePermitList.selectedPermitExternalId)
    );
};

interface CreateContextEnabledProps {
    permitCreateLayout: PermitCreateState.Layout.State;
    permitType: Maybe<PermitTypeState.PermitType>;
}
export const createContextEnabled = (s: CreateContextEnabledProps): boolean => {
    return s.permitCreateLayout.createEnabled;
};

export function PermitSlideIn() {
    const { storeState } = useStore(store => ({
        clearancePermitList: new ClearancePermitListState.StateSlice(store)
            .state,
        permitCreateLayout: PermitCreateState.Layout.get(store),
        permitType: PermitTypeState.getById(
            store,
            PermitCreateState.Config.get(store).permitTypeId,
        ),
        edit: PermitEditState.Edit.get(store),
        editLayout: PermitEditState.Layout.get(store),
        licensePlate: LicensePlateState.Entity.get(store),
        addLp: LicensePlateState.AddTripple.get(store),
        addressSelect: AddressState.Select.get(store),
    }));

    function renderContent(): Maybe<JSX.Element> {
        if (editContextEnabled(storeState)) {
            return <PermitEdit />;
        } else if (createContextEnabled(storeState)) {
            return <PermitCreate />;
        } else {
            return null;
        }
    }

    function isDisabled(): boolean {
        if (editContextEnabled(storeState)) {
            return (
                storeState.editLayout.showPrintSlideIn ||
                isDefined(storeState.licensePlate.id) ||
                storeState.addressSelect.selectAddressOpen
            );
        } else {
            return (
                storeState.addLp.createLpOpen ||
                storeState.permitCreateLayout.vignetteCountSelectionOpen ||
                storeState.permitCreateLayout.validityFromDatePickerOpen ||
                storeState.permitCreateLayout.validityToDatePickerOpen ||
                storeState.permitCreateLayout.validityDurationPickerOpen ||
                storeState.permitCreateLayout.permitTypeSelectionOpen ||
                storeState.permitCreateLayout.zonePickerOpen ||
                isDefined(storeState.licensePlate.id) ||
                storeState.addressSelect.selectAddressOpen
            );
        }
    }

    const content = renderContent();
    return (
        <FullSlideIn
            open={isDefined(content)}
            outsideBody
            disabled={isDisabled()}
        >
            {content}
        </FullSlideIn>
    );
}
